<template>
  <div
    v-click-outside="closeFloatingPanel"
    class="sdp"
    @click.stop
  >
    <button
      ref="trigger"
      class="depth-action-btn sdp__trigger"
      :class="{ active: isOpen || depth.rows > 1 }"
      title="Shelf depth"
      type="button"
      @click="toggleFloatingPanel"
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        stroke-width="2" 
        stroke-linecap="round" 
        stroke-linejoin="round" 
        class="lucide lucide-layers h-3 w-3"
      >
        <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
        <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
        <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
      </svg>
      <span
        class="sdp__badge"
      >{{ depth.rows }}</span>
    </button>

    <transition name="sdp-panel">
      <div
        v-if="isOpen"
        ref="panel"
        class="sdp__panel"
        :style="floatingPanelStyle"
      >
        <div
          v-for="control in controls"
          :key="control.key"
          class="sdp__control"
        >
          <div class="sdp__label">
            <span>{{ control.label }}</span>
            <strong>{{ formatValue(control) }}</strong>
          </div>
          <input
            v-model.number="draft[control.key]"
            type="range"
            :min="control.min"
            :max="control.max"
            :step="control.step"
            @input="emitChange"
          >
        </div>

        <div class="sdp__footer">
          <button
            type="button"
            @click="$emit('apply-all', depth)"
          >
            Apply to all shelves
          </button>
          <button
            type="button"
            @click="reset"
          >
            Reset
          </button>
        </div>
        <div class="sdp__reset-all">
          <button
            type="button"
            title="Reset depth settings for all shelves"
            @click="resetAll"
          >
            Reset
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import fixedDropdownPosition from '@frontend/mixins/fixedDropdownPosition';
import {
  DEFAULT_SHELF_DEPTH,
  MAX_SHELF_DEPTH_RISE,
  MAX_SHELF_DEPTH_ROWS,
  MAX_SHELF_DEPTH_RUN,
  MAX_SHELF_PRODUCT_HEIGHT,
  MIN_SHELF_DEPTH_RISE,
  MIN_SHELF_DEPTH_ROWS,
  MIN_SHELF_DEPTH_RUN,
  MIN_SHELF_PRODUCT_HEIGHT,
  normalizeShelfDepth,
} from '@frontend/components/display-builder/layout-defaults';

export default {
  name: 'ShelfDepthPicker',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._shelfDepthOutside = (event) => {
          if (!el.contains(event.target)) binding.value(event);
        };
        document.addEventListener('mousedown', el._shelfDepthOutside);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el._shelfDepthOutside);
      },
    },
  },
  mixins: [fixedDropdownPosition],
  floatingPanel: {
    gap: 8,
    viewportPadding: 8,
    // Preserve the original `right: -28px` horizontal alignment.
    rightOffset: 28,
  },
  props: {
    value: {
      type: Object,
      default: () => ({ ...DEFAULT_SHELF_DEPTH }),
    },
  },
  data() {
    return {
      isOpen: false,
      draft: normalizeShelfDepth(this.value),
      controls: [
        { key: 'rows', label: 'Depth (rows deep)', min: MIN_SHELF_DEPTH_ROWS, max: MAX_SHELF_DEPTH_ROWS, step: 1, suffix: '' },
        { key: 'risePercent', label: 'Depth height (rise)', min: MIN_SHELF_DEPTH_RISE, max: MAX_SHELF_DEPTH_RISE, step: 1, suffix: '%' },
        { key: 'runPercent', label: 'Depth angle (run)', min: MIN_SHELF_DEPTH_RUN, max: MAX_SHELF_DEPTH_RUN, step: 1, suffix: '%' },
        { key: 'productHeightPercent', label: 'Shelf height', min: MIN_SHELF_PRODUCT_HEIGHT, max: MAX_SHELF_PRODUCT_HEIGHT, step: 1, suffix: '%' },
      ],
    };
  },
  computed: {
    depth() {
      return normalizeShelfDepth(this.draft);
    },
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.draft = normalizeShelfDepth(value);
      },
    },
  },
  methods: {
    emitChange() {
      this.$emit('change', this.depth);
    },
    formatValue(control) {
      return `${this.depth[control.key]}${control.suffix}`;
    },
    reset() {
      this.draft = normalizeShelfDepth(DEFAULT_SHELF_DEPTH);
      this.$emit('reset', this.depth);
      this.$emit('change', this.depth);
    },
    resetAll() {
      this.draft = normalizeShelfDepth(DEFAULT_SHELF_DEPTH);
      this.$emit('reset-all', this.depth);
    },
  },
};
</script>

<style scoped>
:root {
  --teal:       #2eb89a;
  --teal-light: #e8f7f3;
  --teal-dim:   rgba(46,184,154,.12);
  --border:     #e8eaed;
  --border-mid: #d5d9df;
  --bg:         #ffffff;
  --bg-soft:    #f8f9fb;
  --bg-hover:   #f2f4f7;
  --text:       #1a1d23;
  --text-muted: #6b7280;
  --text-light: #9ca3af;
  --danger:     #ef4444;
  --danger-bg:  #fef2f2;
  --radius:     10px;
  --radius-sm:  6px;
  --shadow:     0 1px 3px rgba(0,0,0,.07);
}
.sdp { position: relative; }
.sdp__trigger { position: relative; gap: 0.125rem;}
.sdp__trigger.active { color: #1aa98e; }
.sdp__badge {
  font-size: 9px;
  line-height: 14px;
}
.depth-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-light);
  cursor: pointer;
  transition: background .12s, color .12s;
}
.depth-action-btn:hover { background: var(--bg-hover); color: var(--text-muted); }
.sdp__panel {
  position: fixed;
  z-index: 1100;
  width: 236px;
  padding: 12px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0,0,0,.14);
}
.sdp__control + .sdp__control { margin-top: 12px; }
.sdp__label { display: flex; justify-content: space-between; gap: 10px; font-size: 11px; color: #30343b; font-weight: 600; line-height: 1rem; cursor: default; }
.sdp__label strong { font-weight: 400; font-size: 10px; }
.sdp input[type="range"] {
  width: 100%;
  height: 4px;
  margin-top: 7px;
  accent-color: #20b89a;
  cursor: pointer;
}
.sdp input[type="range"]::-webkit-slider-thumb { cursor: pointer; }
.sdp input[type="range"]::-moz-range-thumb { cursor: pointer; }
.sdp__footer { display: flex; justify-content: space-between; margin-top: 14px; padding-top: 10px; border-top: 1px solid #e8eaed; }
.sdp__footer button { border: 0; padding: 0; font-weight: 600; background: transparent; font-size: 10px; cursor: pointer; }
.sdp__footer button:hover { color: #15977e; }
.sdp__reset-all {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #e8eaed;
}
.sdp__reset-all button {
  border: 0;
  padding: 0;
  color: #30343b;
  background: transparent;
  font-size: 10px;
  cursor: pointer;
}
.sdp__reset-all button:hover { color: #15977e; }
.sdp-panel-enter-active, .sdp-panel-leave-active { transition: opacity .12s ease, transform .12s ease; }
.sdp-panel-enter, .sdp-panel-leave-to { opacity: 0; transform: translateY(-4px); }
.h-3 {
  height: .875rem;
}
.w-3 {
  width: .875rem;
}
</style>
